<template>
    <div class="container">
        <span @click="$emit('close')" role="button"><small><i
                    class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</small></span>
        <h2 class="my-3">{{ $t('modal.manageWorkers') }}</h2>

        <label class="form-label small">{{ $t('modal.currentWorkers') }}</label>
        <div class="card shadow border-0 mb-3">
            <ul class="list-group list-group-flush">
                <li v-for="user in assignedUsers" :key="user.id"
                    class="list-group-item d-flex align-items-center justify-content-between gap-2">
                    <div class="text-break">
                        <span>{{ user.name }}</span>
                        <div v-if="isSelfLocked(user)"
                            class="text-muted small">{{ $t('modal.cannotRemoveSelfActive') }}</div>
                        <!-- The server refuses to remove the last assignee (409). Say so up
                             front rather than leaving a button that can only fail. -->
                        <div v-else-if="canRemove(user) && isLastUser"
                            class="text-muted small">{{ $t('modal.lastWorkerHint') }}</div>
                    </div>
                    <button v-if="canRemove(user) && !isSelfLocked(user)" type="button"
                        class="btn-icon bg-danger-subtle text-danger" :title="$t('common.delete')"
                        :disabled="actionInProgress || isLastUser" @click="removeUser(user)">
                        <i class="fa-regular fa-trash-can"></i>
                    </button>
                </li>
            </ul>
        </div>

        <div class="mb-3">
            <label class="form-label small">{{ $t('modal.addWorkers') }}</label>
            <MultiSelect v-if="groupedOptions.length" v-model="selectedUserIds" :options="groupedOptions"
                optionLabel="name" optionValue="id" optionGroupLabel="label" optionGroupChildren="items"
                :placeholder="$t('modal.selectUsers')" :maxSelectedLabels="3" filter class="w-100 bg-light"
                :showToggleAll="false" :selectedItemsLabel="$t('modal.selectedCount')">
                <template #option="slotProps">
                    <div>
                        <div>{{ slotProps.option.name }}</div>
                        <div v-if="slotProps.option.divisions"
                            class="text-muted small">{{ slotProps.option.divisions }}</div>
                    </div>
                </template>
            </MultiSelect>
            <div v-else-if="loadingUsers" class="fst-italic small">{{ $t('common.loading') }}</div>
            <div v-else class="fst-italic small">{{ $t('modal.noUsersToAdd') }}</div>
        </div>

        <div class="row mt-3">
            <div class="col">
                <button @click="$emit('close')" class="btn border-1 border-primary text-primary w-100"
                    :disabled="actionInProgress">{{ $t('common.cancel') }}</button>
            </div>
            <div class="col">
                <button @click="addUsers" class="btn btn-primary w-100"
                    :disabled="actionInProgress || !selectedUserIds.length">{{ $t('common.add') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import MultiSelect from 'primevue/multiselect';

// The buckets GET users sorts by. Rows arrive ordered by grp then name, so the
// picker only has to preserve that order, never rebuild it.
const GROUP_KEYS = {
    1: 'modal.workerGroupMe',
    2: 'modal.workerGroupDivision',
    3: 'modal.workerGroupOthers',
    4: 'modal.workerGroupThirdParty',
};

export default {
    components: {
        MultiSelect,
    },
    props: {
        taskId: {
            type: Number,
            required: true
        },
        assignedUsers: {
            type: Array,
            default: () => []
        },
        currentUserId: {
            type: Number,
            default: null
        },
        // Priv_AppTasksFollowup. Without it the server only accepts the caller
        // acting on themselves, so the UI offers nothing else.
        canManageOthers: {
            type: Boolean,
            default: false
        },
        // The caller has a running work or transport timer on this task.
        selfLocked: {
            type: Boolean,
            default: false
        },
        // The parent's promise-based Bootstrap confirm; window.confirm is only a
        // fallback for a parent that has none.
        confirm: {
            type: Function,
            default: (message) => Promise.resolve(window.confirm(message))
        },
    },
    emits: ['close', 'users-updated', 'self-removed'],
    data() {
        return {
            allUsers: [],
            selectedUserIds: [],
            loadingUsers: true,
            // Held only while a request is in flight, so a slow network cannot
            // turn one click into two writes.
            actionInProgress: false,
        };
    },
    computed: {
        assignedIds() {
            return new Set(this.assignedUsers.map(user => user.id));
        },
        isLastUser() {
            return this.assignedUsers.length <= 1;
        },
        // GET users does no filtering against the current assignees, so the
        // reconciliation happens here.
        groupedOptions() {
            const groups = [];
            const byGrp = new Map();

            this.allUsers.forEach(user => {
                if (this.assignedIds.has(user.id)) return;
                if (!this.canManageOthers && user.id !== this.currentUserId) return;

                const key = GROUP_KEYS[user.grp];
                if (!key) return;

                if (!byGrp.has(user.grp)) {
                    const group = { key: user.grp, label: this.$t(key), items: [] };
                    byGrp.set(user.grp, group);
                    groups.push(group);
                }
                byGrp.get(user.grp).items.push(user);
            });

            return groups;
        },
    },
    mounted() {
        this.fetchUsers();
    },
    methods: {
        async fetchUsers() {
            this.loadingUsers = true;
            try {
                const response = await axios.get('users');
                this.allUsers = response.data.data;
            } catch (error) {
                console.error('Error fetching users', error);
                this.$root.$refs.globalAlert?.showAlert(this.$t('common.failed'), 'danger');
            } finally {
                this.loadingUsers = false;
            }
        },
        canRemove(user) {
            return this.canManageOthers || user.id === this.currentUserId;
        },
        isSelfLocked(user) {
            return this.selfLocked && user.id === this.currentUserId;
        },
        async addUsers() {
            if (this.actionInProgress || !this.selectedUserIds.length) return;
            this.actionInProgress = true;

            try {
                const response = await axios.post(`tasks/${this.taskId}/users`, {
                    user_ids: this.selectedUserIds,
                });
                // Both routes answer with the task's full updated assignee list,
                // so the parent needs no refetch.
                this.$emit('users-updated', response.data.data);
                this.selectedUserIds = [];
                this.$root.$refs.globalAlert?.showAlert(this.$t('modal.workersUpdated'));
            } catch (error) {
                this.showApiError(error);
            } finally {
                this.actionInProgress = false;
            }
        },
        async removeUser(user) {
            if (this.actionInProgress) return;
            const isSelf = user.id === this.currentUserId;
            if (isSelf && this.selfLocked) return;

            const message = isSelf
                ? this.$t('modal.confirmRemoveSelf')
                : this.$t('modal.confirmRemoveWorker', { name: user.name });
            if (!await this.confirm(message)) return;

            this.actionInProgress = true;
            try {
                const response = await axios.delete(`tasks/${this.taskId}/users/${user.id}`);
                // Removing yourself can cost you access to the task, so the parent
                // navigates away instead of re-rendering it.
                this.$emit(isSelf ? 'self-removed' : 'users-updated', response.data.data);
            } catch (error) {
                this.showApiError(error);
            } finally {
                this.actionInProgress = false;
            }
        },
        // The 409s carry rules the user needs to read ("Task must have at least
        // one assigned user"), so the server message is shown as it came.
        showApiError(error) {
            console.error('Task users request failed', error);
            const message = error?.response?.data?.error?.message || this.$t('common.failed');
            this.$root.$refs.globalAlert?.showAlert(message, 'danger');
            // A 400 Invalid user means the picker is stale — the offending ids are
            // no longer in GET users, so reload it and drop the selection.
            if (error?.response?.status === 400) {
                this.selectedUserIds = [];
                this.fetchUsers();
            }
        },
    },
};
</script>

<style>
.p-multiselect-items {
    padding: 0 !important;
}

.p-multiselect-label {
    font-size: 0.875rem;
    color: var(--bs-body-color);
    font-family: var(--bs-body-font-family);
}

.p-checkbox.p-highlight .p-checkbox-box {
    background: var(--bs-red);
    border: var(--bs-red);
}
</style>
